import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AppLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Sparkles, 
  Radio, 
  Tv, 
  Film, 
  ImageIcon, 
  FileText,
  Copy,
  Check,
  Heart,
  Loader2,
  AlertCircle,
  Camera,
  Settings2,
  MapPin,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Download,
  BookmarkPlus,
  Share2,
  TrendingUp,
  Zap,
  Upload,
  Image as ImageLucide,
  Sparkle,
  Wand2,
  RefreshCw,
  Lightbulb,
  Target,
  Ban,
  Quote,
  Gauge,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Pencil,
  Eye,
  EyeOff,
  User,
  AlertTriangle,
  Trophy,
  MessageCircle,
  PartyPopper,
  Megaphone,
  MessageSquare,
  Hand,
  Brain
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import { PERSONALITY_ARCHETYPES } from '../config/personalityArchetypes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_brandbrain-ai/artifacts/wegfskta_SILLON.png";

// Default configurations
const defaultCameraConfig = {
  shot: "Medium shot",
  pov: "Social Media POV (Phone Camera)",
  lens: "35mm",
  camera: "iPhone 17 Pro Max",
  ratio: "9:16",
  mood: "Natural Casual",
  lighting: "Natural",
  movement: "None",
  atmosphere: "None",
  pores_hd: true,
  film_grain: true,
  lens_imperfections: true,
  sweat_effort: false,
  no_text: true,
  no_logos: true,
  no_brands: true,
};

const defaultCampaignConfig = {
  campaign_name: "",
  context_event: "",
  time_of_day: "Día",
  location_suggested: "",
  wardrobe_suggested: "",
  emotion_base: "",
  hook_pov: "personaje",  // personaje, narrador, interaccion
  // Solution config
  solution_mode: "ai",  // "ai" or "fixed"
  fixed_solution: "",
  // Benefit config
  benefit_mode: "ai",  // "ai" or "fixed"
  fixed_benefit: "",
  // Closing config
  closing_mode: "ai",  // "ai" or "fixed"
  fixed_closing: "",
  // Character Soul - Personality
  personality_id: "",
  personality_traits: "",
  personality_custom: "",
};

const defaultBlock = {
  location: "",
  location_mode: "manual", // "ai", "photo", "manual"
  location_photo: null,
  location_photo_preview: null,
  quantity: 2,
  characters: "Una persona",
  gender: "Mixto",
};

export default function GeneratorPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [useBlocks, setUseBlocks] = useState(false);
  const [improvingId, setImprovingId] = useState(null);
  const coreglFileInputRef = useRef(null);
  const [uploadingCoreglInGenerator, setUploadingCoreglInGenerator] = useState(false);
  
  const [formData, setFormData] = useState({
    brand_id: '',
    medium: 'reel',
    topic: '',
    quantity: 3,
    duration: 'medium',
    model_provider: 'openai',
    model_name: 'gpt-5.2',
    language: language
  });

  const [cameraConfig, setCameraConfig] = useState(defaultCameraConfig);
  const [campaignConfig, setCampaignConfig] = useState(defaultCampaignConfig);
  const [blocks, setBlocks] = useState([{ ...defaultBlock }]);

  // Brief Creativo States
  const [briefStep, setBriefStep] = useState(1);
  const [briefExpanded, setBriefExpanded] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [editedLocations, setEditedLocations] = useState({}); // Track edited locations per idea
  const [editingLocationId, setEditingLocationId] = useState(null); // Track which location is being edited
  const [showJsonId, setShowJsonId] = useState(null); // Track which idea's JSON is visible
  const [enhancingPersonality, setEnhancingPersonality] = useState(false); // Track personality enhancement loading
  
  const [briefData, setBriefData] = useState({
    ideaCentral: '',
    emociones: [],
    emocionesOtro: '',
    hookStyle: [],
    hookStyleOtro: '',
    evitar: [],
    evitarOtro: '',
    hookReferencia: '',
    riesgoCreativo: 'balanceado', // seguro, balanceado, experimental
  });

  // Brief Creativo Options
  const emocionesChips = [
    { id: 'alivio', label: 'Alivio' },
    { id: 'urgencia', label: 'Urgencia' },
    { id: 'identificacion', label: 'Identificación' },
    { id: 'humor', label: 'Humor' },
    { id: 'sorpresa', label: 'Sorpresa' },
    { id: 'envidia', label: 'Envidia sana' },
    { id: 'miedo', label: 'Miedo a perder' },
    { id: 'motivacion', label: 'Motivación' },
    { id: 'curiosidad', label: 'Curiosidad' },
    { id: 'verguenza', label: 'Vergüenza ajena' },
  ];

  const hookStyleChips = [
    { id: 'negativo', label: 'Negativo/problema' },
    { id: 'pregunta', label: 'Pregunta incómoda' },
    { id: 'dato', label: 'Dato sorpresivo' },
    { id: 'confesion', label: 'Confesión personal' },
    { id: 'cotidiana', label: 'Escena cotidiana' },
    { id: 'provocacion', label: 'Provocación/reto' },
    { id: 'humor', label: 'Humor/absurdo' },
    { id: 'positivo', label: 'Positivo/celebración' },
  ];

  const evitarChips = [
    { id: 'motivacionales', label: 'Frases motivacionales' },
    { id: 'precios', label: 'Mencionar precios' },
    { id: 'corporativo', label: 'Tono corporativo' },
    { id: 'positivos', label: 'Hooks positivos genéricos' },
    { id: 'superlativos', label: 'Superlativos' },
    { id: 'humor_forzado', label: 'Humor forzado' },
    { id: 'promesas', label: 'Promesas exageradas' },
    { id: 'jerga', label: 'Jerga técnica' },
  ];

  const riesgoOptions = [
    { value: 'seguro', label: 'Seguro', desc: 'Fórmulas probadas, hooks convencionales positivos' },
    { value: 'balanceado', label: 'Balanceado', desc: 'Mezcla de enfoques convencionales e inesperados' },
    { value: 'experimental', label: 'Experimental', desc: 'Romper patrones, hooks negativos, subvertir expectativas' },
  ];

  const toggleChip = (field, chipId) => {
    setBriefData(prev => ({
      ...prev,
      [field]: prev[field].includes(chipId) 
        ? prev[field].filter(id => id !== chipId)
        : [...prev[field], chipId]
    }));
  };

  const buildBriefText = () => {
    const emocionesText = briefData.emociones.map(e => emocionesChips.find(c => c.id === e)?.label).filter(Boolean).join(', ');
    const hookStyleText = briefData.hookStyle.map(h => hookStyleChips.find(c => c.id === h)?.label).filter(Boolean).join(', ');
    const evitarText = briefData.evitar.map(e => evitarChips.find(c => c.id === e)?.label).filter(Boolean).join(', ');
    
    let brief = '';
    if (briefData.ideaCentral) brief += `IDEA CENTRAL: ${briefData.ideaCentral}\n`;
    if (emocionesText || briefData.emocionesOtro) brief += `EMOCIONES A GENERAR: ${emocionesText}${briefData.emocionesOtro ? `, ${briefData.emocionesOtro}` : ''}\n`;
    if (hookStyleText || briefData.hookStyleOtro) brief += `ESTILO DE HOOK: ${hookStyleText}${briefData.hookStyleOtro ? `, ${briefData.hookStyleOtro}` : ''}\n`;
    if (evitarText || briefData.evitarOtro) brief += `EVITAR ABSOLUTAMENTE: ${evitarText}${briefData.evitarOtro ? `, ${briefData.evitarOtro}` : ''}\n`;
    if (briefData.hookReferencia) brief += `HOOKS DE REFERENCIA: ${briefData.hookReferencia}\n`;
    brief += `NIVEL DE RIESGO CREATIVO: ${riesgoOptions.find(r => r.value === briefData.riesgoCreativo)?.label}\n`;
    
    return brief;
  };

  // Helper functions for Image Prompt Visual Display
  const extractMainProp = (scene) => {
    if (!scene) return null;
    const sceneStr = typeof scene === 'string' ? scene : scene.toString();
    // Look for patterns like "holds [object]", "with [object]", "holding [object]"
    const patterns = [
      /holds?\s+(?:a\s+|an\s+)?([^,\.]+)/i,
      /holding\s+(?:a\s+|an\s+)?([^,\.]+)/i,
      /with\s+(?:a\s+|an\s+)?([^,\.]+)/i,
      /muestra\s+(?:un\s+|una\s+)?([^,\.]+)/i,
      /sosteniendo\s+(?:un\s+|una\s+)?([^,\.]+)/i,
      /con\s+(?:un\s+|una\s+)?([^,\.]+)/i,
    ];
    for (const pattern of patterns) {
      const match = sceneStr.match(pattern);
      if (match && match[1]) {
        return match[1].trim().substring(0, 40);
      }
    }
    return null;
  };

  const detectLocationIncoherence = (prop, location) => {
    if (!prop || !location) return false;
    const propLower = (prop || '').toLowerCase();
    const locationLower = (location || '').toLowerCase();
    
    const paymentKeywords = ['billetera', 'wallet', 'dinero', 'money', 'tarjeta', 'card', 'efectivo', 'cash', 'pago', 'payment', 'billetes', 'monedas', 'cartera'];
    const stadiumKeywords = ['estadio', 'stadium', 'cancha', 'field', 'gradas', 'stands', 'tribuna', 'arena', 'pitch', 'court'];
    
    const hasPropPayment = paymentKeywords.some(kw => propLower.includes(kw));
    const hasLocationStadium = stadiumKeywords.some(kw => locationLower.includes(kw));
    
    return hasPropPayment && hasLocationStadium;
  };

  const getCharacterSummary = (characters) => {
    if (!characters) return 'Personaje';
    const charStr = typeof characters === 'string' ? characters : JSON.stringify(characters);
    // Extract first 50 chars or until first comma/period
    const shortSummary = charStr.split(/[,\.]/)[0].substring(0, 50);
    return shortSummary || 'Personaje';
  };

  const handleLocationEdit = (ideaId, newLocation) => {
    setEditedLocations(prev => ({
      ...prev,
      [ideaId]: newLocation
    }));
    setEditingLocationId(null);
  };

  const getCurrentLocation = (idea) => {
    if (editedLocations[idea.idea_id] !== undefined) {
      return editedLocations[idea.idea_id];
    }
    return idea.image_prompt?.location || '';
  };

  const isLocationEdited = (ideaId) => {
    return editedLocations[ideaId] !== undefined;
  };

  const mediumOptions = [
    { value: 'radio', icon: Radio, label: t('radio') },
    { value: 'tv', icon: Tv, label: t('tv') },
    { value: 'reel', icon: Film, label: t('reel') },
    { value: 'story', icon: ImageIcon, label: t('story') },
    { value: 'post', icon: FileText, label: t('post') },
  ];

  const modelOptions = [
    { provider: 'openai', name: 'gpt-5.2', label: 'GPT-5.2 (OpenAI)' },
    { provider: 'openai', name: 'gpt-4o', label: 'GPT-4o (OpenAI)' },
    { provider: 'anthropic', name: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { provider: 'gemini', name: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
  ];

  const shotOptions = ["Close up", "Medium close up", "Medium shot", "Medium full shot", "Full shot", "Wide shot"];
  const povOptions = ["Social Media POV (Phone Camera)", "Cinematic", "First Person", "Third Person", "Drone/Aerial"];
  const lensOptions = ["24mm", "35mm", "50mm", "85mm", "135mm"];
  const cameraOptions = ["iPhone 17 Pro Max", "iPhone 16 Pro", "Canon R5", "Sony A7IV", "RED Komodo"];
  const ratioOptions = ["9:16", "1:1", "4:5", "16:9", "2.35:1"];
  const moodOptions = ["Natural Casual", "Dramatic", "Energetic", "Calm", "Nostalgic", "Luxurious"];
  const lightingOptions = [
    "Natural",
    "Golden Hour", 
    "Blue Hour",
    "Cinematográfica",
    "Comercial TV",
    "Nocturna",
    "Neón/Urbana",
    "Soft/Difusa",
    "High Key (brillante)",
    "Low Key (oscura)",
    "Contraluz",
    "Luz de ventana"
  ];
  const movementOptions = ["None", "Slow Motion", "Timelapse", "Handheld", "Dolly", "Pan"];
  const atmosphereOptions = ["None", "Golden Hour", "Blue Hour", "Night", "Overcast", "Sunny"];
  const timeOptions = ["Día", "Noche", "Atardecer", "Amanecer", "Madrugada"];
  const hookPovOptions = [
    { value: "personaje", label: language === 'es' ? "Personaje (habla a cámara)" : "Character (speaks to camera)", desc: language === 'es' ? "1era persona, el personaje tiene voz" : "1st person, character has voice" },
    { value: "narrador", label: language === 'es' ? "Narrador (voz en off)" : "Narrator (voice over)", desc: language === 'es' ? "3era persona, personaje mudo" : "3rd person, character is mute" },
    { value: "interaccion", label: language === 'es' ? "Interacción (diálogo)" : "Interaction (dialogue)", desc: language === 'es' ? "Personajes dialogan entre sí" : "Characters dialogue with each other" },
  ];
  const characterOptions = ["Una persona", "Pareja", "Familia", "Amigos", "Cliente + vendedor", "Grupo"];
  const genderOptions = ["Mixto", "Hombre", "Mujer"];
  const durationOptions = [
    { value: 'short', label: language === 'es' ? '15 seg (Corto)' : '15 sec (Short)' },
    { value: 'medium', label: language === 'es' ? '30 seg (Medio)' : '30 sec (Medium)' },
    { value: 'long', label: language === 'es' ? '60 seg (Largo)' : '60 sec (Long)' },
  ];

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`, { withCredentials: true });
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBlock = () => {
    setBlocks([...blocks, { ...defaultBlock }]);
  };

  const removeBlock = (index) => {
    if (blocks.length > 1) {
      setBlocks(blocks.filter((_, i) => i !== index));
    }
  };

  const updateBlock = (index, field, value) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], [field]: value };
    setBlocks(newBlocks);
  };

  const getTotalQuantity = () => {
    if (useBlocks) {
      return blocks.reduce((sum, block) => sum + block.quantity, 0);
    }
    return formData.quantity;
  };

  const getConfirmationSummary = () => {
    return {
      riesgo: riesgoOptions.find(r => r.value === briefData.riesgoCreativo)?.label || 'Balanceado',
      riesgoDesc: riesgoOptions.find(r => r.value === briefData.riesgoCreativo)?.desc || '',
      emociones: briefData.emociones.map(e => emocionesChips.find(c => c.id === e)?.label).filter(Boolean),
      hookStyle: briefData.hookStyle.map(h => hookStyleChips.find(c => c.id === h)?.label).filter(Boolean),
      evitar: briefData.evitar.map(e => evitarChips.find(c => c.id === e)?.label).filter(Boolean),
      ideaCentral: briefData.ideaCentral,
      cantidad: getTotalQuantity(),
    };
  };

  // Function to enhance personality with AI
  const handleEnhancePersonality = async () => {
    if (!campaignConfig.personality_custom.trim()) {
      toast.error('Ingresa una descripción de personalidad para enriquecer');
      return;
    }
    
    setEnhancingPersonality(true);
    try {
      const response = await axios.post(`${API}/enhance-personality`, {
        description: campaignConfig.personality_custom,
        ai_provider: 'anthropic',
        ai_model: 'claude-sonnet-4-20250514'
      }, { withCredentials: true });
      
      setCampaignConfig(prev => ({
        ...prev,
        personality_traits: response.data.enhanced_traits
      }));
      toast.success('Personalidad enriquecida con IA');
    } catch (error) {
      console.error('Error enhancing personality:', error);
      toast.error('Error al enriquecer personalidad');
    } finally {
      setEnhancingPersonality(false);
    }
  };

  // Function to select personality archetype
  const handleSelectPersonality = (archetype) => {
    setCampaignConfig(prev => ({
      ...prev,
      personality_id: archetype.id,
      personality_traits: archetype.traits,
      personality_custom: archetype.id === 'custom' ? prev.personality_custom : ''
    }));
  };

  // Get icon component for personality archetype
  const getPersonalityIcon = (iconName) => {
    const icons = {
      Trophy, Eye, Zap, MessageCircle, PartyPopper, Megaphone, MessageSquare, Hand
    };
    return icons[iconName] || User;
  };

  const [currentModel, setCurrentModel] = useState('openai');
  const [errorDetails, setErrorDetails] = useState(null);

  const handlePreGenerate = (e) => {
    e.preventDefault();
    setErrorDetails(null);
    
    // Validaciones obligatorias
    if (!formData.brand_id) {
      toast.error(language === 'es' ? '⚠️ Campo obligatorio: Selecciona una marca' : '⚠️ Required: Select a brand');
      return;
    }
    if (!formData.topic.trim()) {
      toast.error(language === 'es' ? '⚠️ Campo obligatorio: Ingresa un tema o producto' : '⚠️ Required: Enter a topic or product');
      return;
    }
    
    // Validar bloques si están activos
    if (useBlocks) {
      const emptyBlocks = blocks.filter(b => !b.location.trim());
      if (emptyBlocks.length > 0) {
        toast.error(language === 'es' ? '⚠️ Completa la locación en todos los bloques' : '⚠️ Fill location in all blocks');
        return;
      }
    }

    // Validar Brief Creativo - Step 1 es requerido
    if (!briefData.ideaCentral.trim()) {
      toast.error(language === 'es' ? '⚠️ Completa la idea central en el Brief Creativo' : '⚠️ Complete the central idea in Creative Brief');
      setBriefExpanded(true);
      setBriefStep(1);
      return;
    }

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleGenerate = async () => {
    setShowConfirmModal(false);
    setGenerating(true);
    setGeneratedIdeas([]);
    
    try {
      // Build the creative brief
      const creativeBrief = buildBriefText();
      
      const payload = {
        ...formData,
        language: language,
        camera_config: cameraConfig,
        campaign_config: {
          ...campaignConfig,
          creative_brief: creativeBrief,
          riesgo_creativo: briefData.riesgoCreativo,
        },
        blocks: useBlocks ? blocks : null,
        quantity: useBlocks ? getTotalQuantity() : formData.quantity,
      };

      // Calculate total quantity to determine endpoint
      const totalQty = useBlocks ? getTotalQuantity() : formData.quantity;
      const useBatchEndpoint = totalQty > 5;
      
      const endpoint = useBatchEndpoint ? `${API}/ideas/generate-batch` : `${API}/ideas/generate`;
      
      if (useBatchEndpoint) {
        toast.info(language === 'es' 
          ? `🔄 Generando ${totalQty} ideas en lotes para evitar timeouts...` 
          : `🔄 Generating ${totalQty} ideas in batches to avoid timeouts...`
        );
      }

      const response = await axios.post(endpoint, payload, { withCredentials: true });
      
      setGeneratedIdeas(response.data.ideas || []);
      setCurrentModel(formData.model_provider);
      
      // Show batch results if applicable
      if (useBatchEndpoint && response.data.batches_total) {
        const generated = response.data.total_generated || 0;
        const requested = response.data.total_requested || totalQty;
        const batchesOk = response.data.batches_completed || 0;
        const batchesTotal = response.data.batches_total || 0;
        
        if (response.data.errors && response.data.errors.length > 0) {
          toast.warning(language === 'es' 
            ? `⚠️ ${generated}/${requested} ideas generadas (${batchesOk}/${batchesTotal} lotes exitosos)`
            : `⚠️ ${generated}/${requested} ideas generated (${batchesOk}/${batchesTotal} batches successful)`
          );
        } else {
          toast.success(language === 'es' 
            ? `✅ ${generated} ideas generadas en ${batchesTotal} lotes!`
            : `✅ ${generated} ideas generated in ${batchesTotal} batches!`
          );
        }
      } else {
        toast.success(language === 'es' ? '¡Ideas generadas y guardadas!' : 'Ideas generated and saved!');
      }
    } catch (error) {
      console.error('Error generating ideas:', error);
      const errorMsg = error.response?.data?.detail || '';
      
      // Determinar tipo de error y dar recomendación
      let userMessage = '';
      let recommendation = '';
      
      if (errorMsg.includes('502') || errorMsg.includes('gateway') || errorMsg.includes('no disponible')) {
        userMessage = language === 'es' 
          ? `❌ El servicio de ${formData.model_provider === 'openai' ? 'OpenAI' : formData.model_provider} no está respondiendo.`
          : `❌ ${formData.model_provider === 'openai' ? 'OpenAI' : formData.model_provider} service is not responding.`;
        recommendation = language === 'es'
          ? '💡 Recomendación: Cambia a otro modelo de IA (Claude o Gemini) e intenta de nuevo.'
          : '💡 Recommendation: Switch to another AI model (Claude or Gemini) and try again.';
      } else if (errorMsg.includes('rate') || errorMsg.includes('limit') || errorMsg.includes('429')) {
        userMessage = language === 'es'
          ? '❌ Límite de solicitudes alcanzado.'
          : '❌ Rate limit reached.';
        recommendation = language === 'es'
          ? '💡 Espera 30 segundos o cambia a otro modelo de IA.'
          : '💡 Wait 30 seconds or switch to another AI model.';
      } else if (errorMsg.includes('timeout')) {
        userMessage = language === 'es'
          ? '❌ La solicitud tardó demasiado tiempo.'
          : '❌ Request took too long.';
        recommendation = language === 'es'
          ? '💡 Reduce la cantidad de ideas o cambia de modelo IA.'
          : '💡 Reduce number of ideas or switch AI model.';
      } else {
        userMessage = language === 'es'
          ? `❌ Error al generar: ${errorMsg || 'Error desconocido'}`
          : `❌ Generation error: ${errorMsg || 'Unknown error'}`;
        recommendation = language === 'es'
          ? '💡 Intenta con otro modelo de IA o verifica tu conexión.'
          : '💡 Try another AI model or check your connection.';
      }
      
      setErrorDetails({ message: userMessage, recommendation });
      toast.error(userMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveAllToFavorites = async () => {
    try {
      await Promise.all(
        generatedIdeas
          .filter(idea => !idea.is_favorite)
          .map(idea => axios.put(`${API}/ideas/${idea.idea_id}/favorite`, {}, { withCredentials: true }))
      );
      setGeneratedIdeas(prev => prev.map(idea => ({ ...idea, is_favorite: true })));
      toast.success(language === 'es' ? '✅ Todas las ideas marcadas como favoritas' : '✅ All ideas marked as favorites');
    } catch (error) {
      console.error('Error saving favorites:', error);
      toast.error(language === 'es' ? 'Error al guardar favoritos' : 'Error saving favorites');
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success(t('copied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleToggleFavorite = async (ideaId) => {
    try {
      const response = await axios.put(`${API}/ideas/${ideaId}/favorite`, {}, { withCredentials: true });
      setGeneratedIdeas(prev => prev.map(idea => 
        idea.idea_id === ideaId ? { ...idea, is_favorite: response.data.is_favorite } : idea
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleImproveIdea = async (ideaId) => {
    setImprovingId(ideaId);
    try {
      const response = await axios.post(
        `${API}/ideas/${ideaId}/improve`,
        { model_provider: formData.model_provider, model_name: formData.model_name },
        { withCredentials: true }
      );
      
      const { improved_idea, original_score, new_score, improvement, changes_made } = response.data;
      
      // Add improved idea to the list
      setGeneratedIdeas(prev => [improved_idea, ...prev]);
      
      // Show success message with score improvement
      const improvementText = improvement > 0 
        ? `+${improvement} puntos` 
        : improvement < 0 
          ? `${improvement} puntos` 
          : 'sin cambio';
      
      toast.success(
        language === 'es' 
          ? `✨ ¡Idea mejorada! Score: ${original_score} → ${new_score} (${improvementText})`
          : `✨ Idea improved! Score: ${original_score} → ${new_score} (${improvementText})`,
        { duration: 5000 }
      );
      
      // Expand the improved idea to show changes
      setExpandedIds(prev => new Set([...prev, improved_idea.idea_id]));
      
    } catch (error) {
      console.error('Error improving idea:', error);
      toast.error(language === 'es' ? 'Error al mejorar la idea' : 'Error improving idea');
    } finally {
      setImprovingId(null);
    }
  };

  const handleRegenerateIdea = async (ideaId, toneAdjust = null) => {
    setRegeneratingId(ideaId);
    try {
      const idea = generatedIdeas.find(i => i.idea_id === ideaId);
      if (!idea) return;
      
      const creativeBrief = buildBriefText();
      
      const response = await axios.post(
        `${API}/ideas/regenerate-single`,
        { 
          original_idea_id: ideaId,
          model_provider: formData.model_provider, 
          model_name: formData.model_name,
          creative_brief: creativeBrief,
          riesgo_creativo: briefData.riesgoCreativo,
          tone_adjust: toneAdjust, // 'mas_negativo', 'mas_positivo', 'mas_urgente', 'mas_casual'
        },
        { withCredentials: true }
      );
      
      const { regenerated_idea } = response.data;
      
      // Replace the old idea with the new one in the list
      setGeneratedIdeas(prev => prev.map(i => 
        i.idea_id === ideaId ? regenerated_idea : i
      ));
      
      toast.success(language === 'es' 
        ? `✨ Idea regenerada${toneAdjust ? ' con ajuste de tono' : ''}`
        : `✨ Idea regenerated${toneAdjust ? ' with tone adjustment' : ''}`
      );
      
    } catch (error) {
      console.error('Error regenerating idea:', error);
      toast.error(language === 'es' ? 'Error al regenerar' : 'Error regenerating');
    } finally {
      setRegeneratingId(null);
    }
  };

  // Helper to get selected brand object
  const selectedBrand = brands.find(b => b.brand_id === formData.brand_id);

  // Handle CORE-GL upload from generator
  const handleCoreglUploadFromGenerator = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !formData.brand_id) return;

    setUploadingCoreglInGenerator(true);
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      
      await axios.post(
        `${API}/brands/${formData.brand_id}/coregl`,
        { coregl_data: jsonData },
        { withCredentials: true }
      );
      
      // Update brands list
      setBrands(prev => prev.map(brand => 
        brand.brand_id === formData.brand_id 
          ? { 
              ...brand, 
              coregl_data: jsonData,
              coregl_connected: true,
              coregl_updated_at: new Date().toISOString()
            }
          : brand
      ));
      
      toast.success(language === 'es' ? 'CORE-GL conectado ✓' : 'CORE-GL connected ✓');
      
    } catch (error) {
      console.error('Error uploading CORE-GL:', error);
      const errorMsg = error.response?.data?.detail || 
        (language === 'es' 
          ? 'Archivo no compatible. Exporta desde CORE-GL e intenta de nuevo.' 
          : 'Incompatible file. Export from CORE-GL and try again.');
      toast.error(errorMsg);
    } finally {
      setUploadingCoreglInGenerator(false);
      event.target.value = '';
    }
  };

  const handleExportPDF = (idea) => {
    const brand = brands.find(b => b.brand_id === formData.brand_id);
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(24);
    doc.setTextColor(132, 204, 22);
    doc.text('BRAIN-GL', 20, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Creative Brief', 20, 32);
    
    // Brand info
    doc.setFontSize(16);
    doc.setTextColor(227, 24, 55);
    doc.text(brand?.name || 'Brand', 20, 50);
    
    const mediumLabels = { radio: 'Radio', tv: 'TV', reel: 'Reel', story: 'Story', post: 'Post' };
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Medium: ${mediumLabels[idea.medium] || idea.medium}`, 20, 58);
    doc.text(`Topic: ${idea.topic}`, 20, 65);
    
    let yPos = 85;
    
    doc.setFontSize(12);
    doc.setTextColor(132, 204, 22);
    doc.text('HOOK / PROBLEM', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const hookLines = doc.splitTextToSize(idea.hook, 170);
    doc.text(hookLines, 20, yPos + 8);
    yPos += 8 + (hookLines.length * 5) + 10;
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('SOLUTION', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const solutionLines = doc.splitTextToSize(idea.solution, 170);
    doc.text(solutionLines, 20, yPos + 8);
    yPos += 8 + (solutionLines.length * 5) + 10;
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('BENEFIT', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const benefitLines = doc.splitTextToSize(idea.benefit, 170);
    doc.text(benefitLines, 20, yPos + 8);
    yPos += 8 + (benefitLines.length * 5) + 10;
    
    doc.setFontSize(12);
    doc.setTextColor(227, 24, 55);
    doc.text('CLOSING', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const closingLines = doc.splitTextToSize(idea.closing, 170);
    doc.text(closingLines, 20, yPos + 8);
    yPos += 8 + (closingLines.length * 5) + 15;
    
    if (yPos < 250) {
      doc.setFontSize(12);
      doc.setTextColor(132, 204, 22);
      doc.text('FULL COPY', 20, yPos);
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      const copyLines = doc.splitTextToSize(idea.full_copy, 170);
      doc.text(copyLines, 20, yPos + 8);
    }
    
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated with Brain-GL - ${new Date().toLocaleDateString()}`, 20, 285);
    
    doc.save(`${brand?.name || 'brand'}_${idea.medium}_copy.pdf`);
    toast.success(language === 'es' ? 'PDF exportado' : 'PDF exported');
  };

  const toggleExpanded = (ideaId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ideaId)) {
        newSet.delete(ideaId);
      } else {
        newSet.add(ideaId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in" data-testid="generator-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {t('generateCopy')}
          </h1>
          <p className="text-zinc-400 mt-1">
            {language === 'es' ? 'Crea copys creativos con inteligencia artificial' : 'Create creative copies with artificial intelligence'}
          </p>
        </div>

        {brands.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <AlertCircle className="w-12 h-12 text-zinc-500 mb-4" />
              <p className="text-zinc-400 text-center mb-4">{t('noBrands')}</p>
              <Button 
                onClick={() => navigate('/brands')}
                className="bg-lime-500 text-black hover:bg-lime-600 font-medium"
              >
                {t('createFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Left Panel - Configuration */}
            <div className="lg:col-span-4 space-y-4">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="space-y-4 pr-4">
                  {/* Required Fields Notice */}
                  <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-xs text-red-400">
                      <span className="text-red-500 font-bold">*</span> {language === 'es' ? 'Campos obligatorios' : 'Required fields'}
                    </p>
                  </div>

                  {/* Brief Creativo Wizard */}
                  <Collapsible open={briefExpanded} onOpenChange={setBriefExpanded}>
                    <Card className="bg-gradient-to-br from-purple-900/30 to-zinc-900/50 border-purple-500/30">
                      <CollapsibleTrigger asChild>
                        <CardHeader className="pb-2 cursor-pointer hover:bg-purple-500/5 transition-colors">
                          <CardTitle className="text-sm font-semibold text-white flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Lightbulb className="w-4 h-4 text-purple-400" />
                              <span>Brief Creativo</span>
                              <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-[10px]">
                                {language === 'es' ? 'Alta Prioridad' : 'High Priority'}
                              </Badge>
                            </div>
                            {briefExpanded ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                          </CardTitle>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="space-y-4 pt-0">
                          {/* Progress Bar */}
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs text-zinc-500">
                              <span>Paso {briefStep}/6</span>
                              <span>{Math.round((briefStep / 6) * 100)}%</span>
                            </div>
                            <Progress value={(briefStep / 6) * 100} className="h-1 bg-zinc-800" />
                            {/* Dot Navigation */}
                            <div className="flex justify-center gap-2">
                              {[1, 2, 3, 4, 5, 6].map((step) => (
                                <button
                                  key={step}
                                  onClick={() => setBriefStep(step)}
                                  className={`w-2 h-2 rounded-full transition-colors ${
                                    briefStep === step ? 'bg-purple-500' : 
                                    briefStep > step ? 'bg-purple-500/50' : 'bg-zinc-700'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Step 1 - Idea Central */}
                          {briefStep === 1 && (
                            <div className="space-y-3 animate-fade-in">
                              <div className="flex items-center gap-2">
                                <Target className="w-4 h-4 text-purple-400" />
                                <Label className="text-sm font-medium text-white">
                                  ¿Cuál es la idea central? <span className="text-red-500">*</span>
                                </Label>
                              </div>
                              <Textarea
                                value={briefData.ideaCentral}
                                onChange={(e) => setBriefData({...briefData, ideaCentral: e.target.value})}
                                placeholder="Explícala como se la contarías a un amigo en 2 oraciones..."
                                className="min-h-[100px] bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
                              />
                            </div>
                          )}

                          {/* Step 2 - Emociones */}
                          {briefStep === 2 && (
                            <div className="space-y-3 animate-fade-in">
                              <div className="flex items-center gap-2">
                                <Heart className="w-4 h-4 text-purple-400" />
                                <Label className="text-sm font-medium text-white">
                                  ¿Qué quieres que sienta el espectador? <span className="text-red-500">*</span>
                                </Label>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {emocionesChips.map((chip) => (
                                  <button
                                    key={chip.id}
                                    type="button"
                                    onClick={() => toggleChip('emociones', chip.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                      briefData.emociones.includes(chip.id)
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                    }`}
                                  >
                                    {chip.label}
                                  </button>
                                ))}
                              </div>
                              <Input
                                value={briefData.emocionesOtro}
                                onChange={(e) => setBriefData({...briefData, emocionesOtro: e.target.value})}
                                placeholder="Otra emoción..."
                                className="bg-zinc-800/50 border-zinc-700 text-white text-sm"
                              />
                            </div>
                          )}

                          {/* Step 3 - Hook Style */}
                          {briefStep === 3 && (
                            <div className="space-y-3 animate-fade-in">
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-purple-400" />
                                <Label className="text-sm font-medium text-white">
                                  ¿Cómo debe arrancar el hook? <span className="text-red-500">*</span>
                                </Label>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {hookStyleChips.map((chip) => (
                                  <button
                                    key={chip.id}
                                    type="button"
                                    onClick={() => toggleChip('hookStyle', chip.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                      briefData.hookStyle.includes(chip.id)
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                    }`}
                                  >
                                    {chip.label}
                                  </button>
                                ))}
                              </div>
                              <Input
                                value={briefData.hookStyleOtro}
                                onChange={(e) => setBriefData({...briefData, hookStyleOtro: e.target.value})}
                                placeholder="Otro estilo..."
                                className="bg-zinc-800/50 border-zinc-700 text-white text-sm"
                              />
                            </div>
                          )}

                          {/* Step 4 - Qué evitar */}
                          {briefStep === 4 && (
                            <div className="space-y-3 animate-fade-in">
                              <div className="flex items-center gap-2">
                                <Ban className="w-4 h-4 text-red-400" />
                                <Label className="text-sm font-medium text-white">
                                  ¿Qué debe evitar absolutamente?
                                </Label>
                                <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-500">Opcional</Badge>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {evitarChips.map((chip) => (
                                  <button
                                    key={chip.id}
                                    type="button"
                                    onClick={() => toggleChip('evitar', chip.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                      briefData.evitar.includes(chip.id)
                                        ? 'bg-red-500/80 text-white'
                                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                    }`}
                                  >
                                    {chip.label}
                                  </button>
                                ))}
                              </div>
                              <Input
                                value={briefData.evitarOtro}
                                onChange={(e) => setBriefData({...briefData, evitarOtro: e.target.value})}
                                placeholder="Otras cosas a evitar..."
                                className="bg-zinc-800/50 border-zinc-700 text-white text-sm"
                              />
                            </div>
                          )}

                          {/* Step 5 - Hook Referencia */}
                          {briefStep === 5 && (
                            <div className="space-y-3 animate-fade-in">
                              <div className="flex items-center gap-2">
                                <Quote className="w-4 h-4 text-purple-400" />
                                <Label className="text-sm font-medium text-white">
                                  ¿Tienes un hook de referencia?
                                </Label>
                                <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-500">Opcional</Badge>
                              </div>
                              <Textarea
                                value={briefData.hookReferencia}
                                onChange={(e) => setBriefData({...briefData, hookReferencia: e.target.value})}
                                placeholder="Pega 2-3 ejemplos de hooks que admires o que hayan funcionado antes..."
                                className="min-h-[80px] bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 text-sm"
                              />
                            </div>
                          )}

                          {/* Step 6 - Riesgo Creativo */}
                          {briefStep === 6 && (
                            <div className="space-y-3 animate-fade-in">
                              <div className="flex items-center gap-2">
                                <Gauge className="w-4 h-4 text-purple-400" />
                                <Label className="text-sm font-medium text-white">
                                  ¿Qué tan arriesgado puede ser? <span className="text-red-500">*</span>
                                </Label>
                              </div>
                              <div className="space-y-2">
                                {riesgoOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setBriefData({...briefData, riesgoCreativo: option.value})}
                                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                                      briefData.riesgoCreativo === option.value
                                        ? option.value === 'seguro' 
                                          ? 'bg-green-500/20 border-green-500/50 text-green-400'
                                          : option.value === 'experimental'
                                            ? 'bg-red-500/20 border-red-500/50 text-red-400'
                                            : 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                                        : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:bg-zinc-700/50'
                                    }`}
                                  >
                                    <div className="font-medium text-sm">{option.label}</div>
                                    <div className="text-xs opacity-70 mt-1">{option.desc}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Navigation Buttons */}
                          <div className="flex justify-between pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setBriefStep(Math.max(1, briefStep - 1))}
                              disabled={briefStep === 1}
                              className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                            >
                              <ArrowLeft className="w-4 h-4 mr-1" />
                              Anterior
                            </Button>
                            {briefStep < 6 ? (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => setBriefStep(Math.min(6, briefStep + 1))}
                                className="bg-purple-500 hover:bg-purple-600 text-white"
                              >
                                Siguiente
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => setBriefExpanded(false)}
                                className="bg-green-500 hover:bg-green-600 text-black"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Completado
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>

                  {/* Brand & Basic Config */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-lime-500" />
                        {language === 'es' ? 'Configuración Básica' : 'Basic Configuration'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Brand */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium uppercase text-zinc-500">
                          {t('selectBrand')} <span className="text-red-500">*</span>
                        </Label>
                        <Select 
                          value={formData.brand_id} 
                          onValueChange={(value) => setFormData({...formData, brand_id: value})}
                        >
                          <SelectTrigger className={`bg-zinc-800/50 text-white ${!formData.brand_id ? 'border-red-500/50' : 'border-zinc-700'}`} data-testid="brand-select">
                            <SelectValue placeholder={language === 'es' ? '⚠️ Selecciona una marca' : '⚠️ Select a brand'} />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {brands.map((brand) => (
                              <SelectItem key={brand.brand_id} value={brand.brand_id} className="text-zinc-300">
                                {brand.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* CORE-GL Status */}
                        {formData.brand_id && (
                          <div className="mt-2">
                            {selectedBrand?.coregl_connected ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                CORE-GL conectado ✓
                              </Badge>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => coreglFileInputRef.current?.click()}
                                  disabled={uploadingCoreglInGenerator}
                                  className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                                >
                                  {uploadingCoreglInGenerator ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Plus className="w-3 h-3" />
                                  )}
                                  Importar análisis CORE-GL
                                </button>
                                <input
                                  ref={coreglFileInputRef}
                                  type="file"
                                  accept=".json"
                                  onChange={handleCoreglUploadFromGenerator}
                                  className="hidden"
                                />
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Medium */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium uppercase text-zinc-500">
                          {t('selectMedium')} <span className="text-red-500">*</span>
                        </Label>
                        <div className="grid grid-cols-5 gap-1">
                          {mediumOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setFormData({...formData, medium: option.value})}
                                className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors ${
                                  formData.medium === option.value
                                    ? 'bg-lime-500/10 border-lime-500/50 text-lime-500'
                                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                }`}
                              >
                                <Icon className="w-4 h-4" />
                                <span className="text-[10px]">{option.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium uppercase text-zinc-500">
                          {language === 'es' ? 'Duración' : 'Duration'}
                        </Label>
                        <Select value={formData.duration} onValueChange={(value) => setFormData({...formData, duration: value})}>
                          <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {durationOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} className="text-zinc-300">{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Topic */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium uppercase text-zinc-500">
                          {t('topic')} <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          value={formData.topic}
                          onChange={(e) => setFormData({...formData, topic: e.target.value})}
                          placeholder={language === 'es' ? '⚠️ Describe el producto, servicio o tema...' : '⚠️ Describe the product, service or topic...'}
                          className={`bg-zinc-800/50 text-white placeholder:text-zinc-500 focus:border-lime-500 min-h-[80px] ${!formData.topic.trim() ? 'border-red-500/50' : 'border-zinc-700'}`}
                          data-testid="topic-input"
                        />
                      </div>

                      {/* Quantity (if not using blocks) */}
                      {!useBlocks && (
                        <div className="space-y-2">
                          <Label className="text-xs font-medium uppercase text-zinc-500">{t('quantity')}</Label>
                          <Select value={String(formData.quantity)} onValueChange={(value) => setFormData({...formData, quantity: parseInt(value)})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {[1,2,3,4,5,6,7,8,9,10].map((num) => (
                                <SelectItem key={num} value={String(num)} className="text-zinc-300">{num} copies</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* AI Model */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium uppercase text-zinc-500">{t('aiModel')}</Label>
                        <Select 
                          value={`${formData.model_provider}:${formData.model_name}`} 
                          onValueChange={(value) => {
                            const [provider, name] = value.split(':');
                            setFormData({...formData, model_provider: provider, model_name: name});
                          }}
                        >
                          <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {modelOptions.map((model) => (
                              <SelectItem key={`${model.provider}:${model.name}`} value={`${model.provider}:${model.name}`} className="text-zinc-300">
                                {model.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Campaign Config */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                        <Settings2 className="w-4 h-4 text-lime-500" />
                        {language === 'es' ? 'Configuración de Campaña' : 'Campaign Configuration'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">
                            {language === 'es' ? 'Nombre Campaña' : 'Campaign Name'}
                          </Label>
                          <Input
                            value={campaignConfig.campaign_name}
                            onChange={(e) => setCampaignConfig({...campaignConfig, campaign_name: e.target.value})}
                            placeholder="Ej: Verano 2026"
                            className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">
                            {language === 'es' ? 'Contexto/Evento' : 'Context/Event'}
                          </Label>
                          <Input
                            value={campaignConfig.context_event}
                            onChange={(e) => setCampaignConfig({...campaignConfig, context_event: e.target.value})}
                            placeholder="Ej: Fútbol, Navidad"
                            className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-9"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">
                            {language === 'es' ? 'Momento del Día' : 'Time of Day'}
                          </Label>
                          <Select value={campaignConfig.time_of_day} onValueChange={(v) => setCampaignConfig({...campaignConfig, time_of_day: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {timeOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">
                            {language === 'es' ? 'Emoción Base' : 'Base Emotion'}
                          </Label>
                          <Input
                            value={campaignConfig.emotion_base}
                            onChange={(e) => setCampaignConfig({...campaignConfig, emotion_base: e.target.value})}
                            placeholder="Ej: Alegre, Urgente"
                            className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-9"
                          />
                        </div>
                      </div>

                      {/* Hook POV - Critical for production */}
                      <div className="pt-2 border-t border-zinc-800 space-y-2">
                        <Label className="text-xs font-medium uppercase text-lime-500">
                          {language === 'es' ? 'Punto de Vista del Hook' : 'Hook Point of View'}
                        </Label>
                        <div className="grid grid-cols-1 gap-2">
                          {hookPovOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setCampaignConfig({...campaignConfig, hook_pov: option.value})}
                              className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors ${
                                campaignConfig.hook_pov === option.value
                                  ? 'bg-lime-500/10 border-lime-500/50'
                                  : 'bg-zinc-800/30 border-zinc-700 hover:border-zinc-600'
                              }`}
                            >
                              <span className={`text-sm font-medium ${campaignConfig.hook_pov === option.value ? 'text-lime-500' : 'text-zinc-300'}`}>
                                {option.label}
                              </span>
                              <span className="text-[10px] text-zinc-500">{option.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">
                            {language === 'es' ? 'Ubicación Sugerida' : 'Suggested Location'}
                          </Label>
                          <Input
                            value={campaignConfig.location_suggested}
                            onChange={(e) => setCampaignConfig({...campaignConfig, location_suggested: e.target.value})}
                            placeholder="Ej: Guatemala"
                            className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">
                            {language === 'es' ? 'Vestimenta' : 'Wardrobe'}
                          </Label>
                          <Input
                            value={campaignConfig.wardrobe_suggested}
                            onChange={(e) => setCampaignConfig({...campaignConfig, wardrobe_suggested: e.target.value})}
                            placeholder="Ej: Casual"
                            className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-9"
                          />
                        </div>
                      </div>

                      {/* Fixed Messages with AI/Fixed toggle */}
                      <div className="pt-2 border-t border-zinc-800 space-y-3">
                        <p className="text-xs font-medium text-white uppercase">
                          {language === 'es' ? 'Mensajes del Copy' : 'Copy Messages'}
                        </p>
                        
                        {/* Solution */}
                        <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] font-medium uppercase text-zinc-400">
                              {language === 'es' ? 'Solución' : 'Solution'}
                            </Label>
                            <div className="flex items-center gap-1 bg-zinc-900 rounded-full p-0.5">
                              <button
                                type="button"
                                onClick={() => setCampaignConfig({...campaignConfig, solution_mode: 'ai'})}
                                className={`px-2 py-1 text-[10px] rounded-full transition-colors ${
                                  campaignConfig.solution_mode === 'ai' 
                                    ? 'bg-lime-500 text-black font-medium' 
                                    : 'text-zinc-400 hover:text-zinc-300'
                                }`}
                              >
                                IA
                              </button>
                              <button
                                type="button"
                                onClick={() => setCampaignConfig({...campaignConfig, solution_mode: 'fixed'})}
                                className={`px-2 py-1 text-[10px] rounded-full transition-colors ${
                                  campaignConfig.solution_mode === 'fixed' 
                                    ? 'bg-red-500 text-white font-medium' 
                                    : 'text-zinc-400 hover:text-zinc-300'
                                }`}
                              >
                                FIJO
                              </button>
                            </div>
                          </div>
                          {campaignConfig.solution_mode === 'fixed' ? (
                            <Textarea
                              value={campaignConfig.fixed_solution}
                              onChange={(e) => setCampaignConfig({...campaignConfig, fixed_solution: e.target.value})}
                              placeholder={language === 'es' ? 'Mensaje institucional exacto...' : 'Exact institutional message...'}
                              className="bg-zinc-800/50 border-red-500/30 text-white text-sm min-h-[60px]"
                            />
                          ) : (
                            <p className="text-[10px] text-lime-500/70 italic">
                              {language === 'es' ? 'La IA generará la solución creativamente' : 'AI will generate the solution creatively'}
                            </p>
                          )}
                        </div>

                        {/* Benefit */}
                        <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] font-medium uppercase text-zinc-400">
                              {language === 'es' ? 'Beneficio' : 'Benefit'}
                            </Label>
                            <div className="flex items-center gap-1 bg-zinc-900 rounded-full p-0.5">
                              <button
                                type="button"
                                onClick={() => setCampaignConfig({...campaignConfig, benefit_mode: 'ai'})}
                                className={`px-2 py-1 text-[10px] rounded-full transition-colors ${
                                  campaignConfig.benefit_mode === 'ai' 
                                    ? 'bg-lime-500 text-black font-medium' 
                                    : 'text-zinc-400 hover:text-zinc-300'
                                }`}
                              >
                                IA
                              </button>
                              <button
                                type="button"
                                onClick={() => setCampaignConfig({...campaignConfig, benefit_mode: 'fixed'})}
                                className={`px-2 py-1 text-[10px] rounded-full transition-colors ${
                                  campaignConfig.benefit_mode === 'fixed' 
                                    ? 'bg-red-500 text-white font-medium' 
                                    : 'text-zinc-400 hover:text-zinc-300'
                                }`}
                              >
                                FIJO
                              </button>
                            </div>
                          </div>
                          {campaignConfig.benefit_mode === 'fixed' ? (
                            <Input
                              value={campaignConfig.fixed_benefit}
                              onChange={(e) => setCampaignConfig({...campaignConfig, fixed_benefit: e.target.value})}
                              placeholder={language === 'es' ? 'Beneficio exacto...' : 'Exact benefit...'}
                              className="bg-zinc-800/50 border-red-500/30 text-white text-sm h-9"
                            />
                          ) : (
                            <p className="text-[10px] text-lime-500/70 italic">
                              {language === 'es' ? 'La IA generará el beneficio creativamente' : 'AI will generate the benefit creatively'}
                            </p>
                          )}
                        </div>

                        {/* Closing */}
                        <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] font-medium uppercase text-zinc-400">
                              {language === 'es' ? 'Cierre' : 'Closing'}
                            </Label>
                            <div className="flex items-center gap-1 bg-zinc-900 rounded-full p-0.5">
                              <button
                                type="button"
                                onClick={() => setCampaignConfig({...campaignConfig, closing_mode: 'ai'})}
                                className={`px-2 py-1 text-[10px] rounded-full transition-colors ${
                                  campaignConfig.closing_mode === 'ai' 
                                    ? 'bg-lime-500 text-black font-medium' 
                                    : 'text-zinc-400 hover:text-zinc-300'
                                }`}
                              >
                                IA
                              </button>
                              <button
                                type="button"
                                onClick={() => setCampaignConfig({...campaignConfig, closing_mode: 'fixed'})}
                                className={`px-2 py-1 text-[10px] rounded-full transition-colors ${
                                  campaignConfig.closing_mode === 'fixed' 
                                    ? 'bg-red-500 text-white font-medium' 
                                    : 'text-zinc-400 hover:text-zinc-300'
                                }`}
                              >
                                FIJO
                              </button>
                            </div>
                          </div>
                          {campaignConfig.closing_mode === 'fixed' ? (
                            <Input
                              value={campaignConfig.fixed_closing}
                              onChange={(e) => setCampaignConfig({...campaignConfig, fixed_closing: e.target.value})}
                              placeholder={language === 'es' ? 'Cierre/CTA exacto con slogan...' : 'Exact closing/CTA with slogan...'}
                              className="bg-zinc-800/50 border-red-500/30 text-white text-sm h-9"
                            />
                          ) : (
                            <p className="text-[10px] text-lime-500/70 italic">
                              {language === 'es' ? 'La IA generará el cierre creativamente' : 'AI will generate the closing creatively'}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Character Soul - Personality */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-500" />
                        {language === 'es' ? 'Alma del Personaje' : 'Character Soul'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-xs text-zinc-400">
                        {language === 'es' 
                          ? 'Define la personalidad visual del personaje en las imágenes' 
                          : 'Define the visual personality of the character in images'}
                      </p>
                      
                      {/* Personality Archetypes Grid */}
                      <div className="grid grid-cols-4 gap-2">
                        {PERSONALITY_ARCHETYPES.map((archetype) => {
                          const IconComponent = getPersonalityIcon(archetype.icon);
                          const isSelected = campaignConfig.personality_id === archetype.id;
                          return (
                            <button
                              key={archetype.id}
                              type="button"
                              onClick={() => handleSelectPersonality(archetype)}
                              className={`p-2 rounded-lg border transition-all text-center ${
                                isSelected
                                  ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                                  : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-purple-500/50 hover:text-purple-400'
                              }`}
                            >
                              <IconComponent className="w-4 h-4 mx-auto mb-1" />
                              <p className="text-[10px] font-medium">{archetype.name}</p>
                              <p className="text-[8px] text-zinc-500">{archetype.desc}</p>
                            </button>
                          );
                        })}
                      </div>

                      {/* Custom Personality Input */}
                      {campaignConfig.personality_id === 'custom' && (
                        <div className="space-y-2 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                          <Label className="text-[10px] font-medium uppercase text-purple-400">
                            {language === 'es' ? 'Describe la personalidad' : 'Describe the personality'}
                          </Label>
                          <Textarea
                            value={campaignConfig.personality_custom}
                            onChange={(e) => setCampaignConfig({...campaignConfig, personality_custom: e.target.value})}
                            placeholder="Ej: Tímido pero determinado, sonrisa nerviosa, evita contacto visual directo..."
                            className="bg-zinc-800/50 border-purple-500/30 text-white text-sm min-h-[60px]"
                          />
                          <Button
                            type="button"
                            onClick={handleEnhancePersonality}
                            disabled={enhancingPersonality || !campaignConfig.personality_custom.trim()}
                            className="w-full bg-purple-500/20 border border-purple-500/50 text-purple-300 hover:bg-purple-500/30"
                          >
                            {enhancingPersonality ? (
                              <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Enriqueciendo...</>
                            ) : (
                              <><Wand2 className="w-3 h-3 mr-2" /> Enriquecer con IA</>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Show selected personality traits */}
                      {campaignConfig.personality_traits && (
                        <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="w-3 h-3 text-purple-400" />
                            <span className="text-[10px] font-medium text-purple-400 uppercase">Traits Activos</span>
                          </div>
                          <p className="text-xs text-zinc-300 leading-relaxed">
                            {campaignConfig.personality_traits}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Camera Config */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                        <Camera className="w-4 h-4 text-lime-500" />
                        {language === 'es' ? 'Dirección de Cámara' : 'Camera Direction'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">Plano (Shot)</Label>
                          <Select value={cameraConfig.shot} onValueChange={(v) => setCameraConfig({...cameraConfig, shot: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {shotOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">POV/Ángulo</Label>
                          <Select value={cameraConfig.pov} onValueChange={(v) => setCameraConfig({...cameraConfig, pov: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {povOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">Lente</Label>
                          <Select value={cameraConfig.lens} onValueChange={(v) => setCameraConfig({...cameraConfig, lens: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {lensOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">Cámara</Label>
                          <Select value={cameraConfig.camera} onValueChange={(v) => setCameraConfig({...cameraConfig, camera: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {cameraOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">Ratio</Label>
                          <Select value={cameraConfig.ratio} onValueChange={(v) => setCameraConfig({...cameraConfig, ratio: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {ratioOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Lighting Row */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">{language === 'es' ? 'Iluminación' : 'Lighting'}</Label>
                          <Select value={cameraConfig.lighting} onValueChange={(v) => setCameraConfig({...cameraConfig, lighting: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {lightingOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">Mood</Label>
                          <Select value={cameraConfig.mood} onValueChange={(v) => setCameraConfig({...cameraConfig, mood: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {moodOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">{language === 'es' ? 'Movimiento' : 'Movement'}</Label>
                          <Select value={cameraConfig.movement} onValueChange={(v) => setCameraConfig({...cameraConfig, movement: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {movementOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-medium uppercase text-zinc-500">{language === 'es' ? 'Atmósfera' : 'Atmosphere'}</Label>
                          <Select value={cameraConfig.atmosphere} onValueChange={(v) => setCameraConfig({...cameraConfig, atmosphere: v})}>
                            <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {atmosphereOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Realism Checkboxes */}
                      <div className="pt-2 border-t border-zinc-800">
                        <p className="text-[10px] font-medium uppercase text-zinc-500 mb-2">Realismo</p>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.pores_hd} onCheckedChange={(c) => setCameraConfig({...cameraConfig, pores_hd: c})} />
                            Poros/piel HD
                          </label>
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.film_grain} onCheckedChange={(c) => setCameraConfig({...cameraConfig, film_grain: c})} />
                            Grano cine
                          </label>
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.lens_imperfections} onCheckedChange={(c) => setCameraConfig({...cameraConfig, lens_imperfections: c})} />
                            Imperfecciones lente
                          </label>
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.sweat_effort} onCheckedChange={(c) => setCameraConfig({...cameraConfig, sweat_effort: c})} />
                            Sudor/esfuerzo
                          </label>
                        </div>
                      </div>

                      {/* Negatives Checkboxes */}
                      <div className="pt-2 border-t border-zinc-800">
                        <p className="text-[10px] font-medium uppercase text-red-500 mb-2">{language === 'es' ? 'Negativos (evitar)' : 'Negatives (avoid)'}</p>
                        <div className="grid grid-cols-3 gap-2">
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.no_text} onCheckedChange={(c) => setCameraConfig({...cameraConfig, no_text: c})} />
                            Sin textos
                          </label>
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.no_logos} onCheckedChange={(c) => setCameraConfig({...cameraConfig, no_logos: c})} />
                            Sin logos
                          </label>
                          <label className="flex items-center gap-2 text-xs text-zinc-300">
                            <Checkbox checked={cameraConfig.no_brands} onCheckedChange={(c) => setCameraConfig({...cameraConfig, no_brands: c})} />
                            Sin marcas
                          </label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Blocks Config */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-lime-500" />
                          {language === 'es' ? 'Plan de Bloques' : 'Block Plan'}
                        </CardTitle>
                        <label className="flex items-center gap-2 text-xs text-zinc-300">
                          <Checkbox checked={useBlocks} onCheckedChange={setUseBlocks} />
                          {language === 'es' ? 'Activar' : 'Enable'}
                        </label>
                      </div>
                    </CardHeader>
                    {useBlocks && (
                      <CardContent className="space-y-3">
                        <p className="text-xs text-zinc-500">
                          {language === 'es' 
                            ? 'Cada bloque genera ideas en esa locación con esos personajes'
                            : 'Each block generates ideas in that location with those characters'}
                        </p>
                        {blocks.map((block, index) => (
                          <div key={index} className="p-3 bg-zinc-800/30 rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-lime-500">
                                {language === 'es' ? `Locación #${index + 1}` : `Location #${index + 1}`}
                              </span>
                              {blocks.length > 1 && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-400 hover:text-red-500" onClick={() => removeBlock(index)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            
                            {/* Location Mode Selector */}
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => updateBlock(index, 'location_mode', 'ai')}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                                  block.location_mode === 'ai'
                                    ? 'bg-lime-500/20 border border-lime-500/50 text-lime-400'
                                    : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                }`}
                              >
                                <Sparkle className="w-3 h-3" />
                                IA
                              </button>
                              <button
                                type="button"
                                onClick={() => updateBlock(index, 'location_mode', 'photo')}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                                  block.location_mode === 'photo'
                                    ? 'bg-lime-500/20 border border-lime-500/50 text-lime-400'
                                    : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                }`}
                              >
                                <ImageLucide className="w-3 h-3" />
                                Foto
                              </button>
                              <button
                                type="button"
                                onClick={() => updateBlock(index, 'location_mode', 'manual')}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                                  block.location_mode === 'manual'
                                    ? 'bg-lime-500/20 border border-lime-500/50 text-lime-400'
                                    : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                }`}
                              >
                                <MapPin className="w-3 h-3" />
                                Manual
                              </button>
                            </div>
                            
                            {/* Location Input based on mode */}
                            {block.location_mode === 'ai' && (
                              <div className="p-2 bg-lime-500/10 border border-lime-500/20 rounded text-xs text-lime-400 text-center">
                                {language === 'es' 
                                  ? 'La IA determinará la locación según la idea' 
                                  : 'AI will determine location based on the idea'}
                              </div>
                            )}
                            
                            {block.location_mode === 'photo' && (
                              <div className="space-y-2">
                                {block.location_photo_preview ? (
                                  <div className="relative">
                                    <img 
                                      src={block.location_photo_preview} 
                                      alt="Reference" 
                                      className="w-full h-24 object-cover rounded border border-zinc-700"
                                    />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="absolute top-1 right-1 h-6 w-6 bg-black/50 text-white hover:bg-red-500/50"
                                      onClick={() => {
                                        updateBlock(index, 'location_photo', null);
                                        updateBlock(index, 'location_photo_preview', null);
                                      }}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-zinc-700 rounded cursor-pointer hover:border-lime-500/50 transition-colors">
                                    <Upload className="w-5 h-5 text-zinc-500 mb-1" />
                                    <span className="text-xs text-zinc-500">
                                      {language === 'es' ? 'Subir foto de referencia' : 'Upload reference photo'}
                                    </span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                            updateBlock(index, 'location_photo', file);
                                            updateBlock(index, 'location_photo_preview', reader.result);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                    />
                                  </label>
                                )}
                                <Input
                                  value={block.location}
                                  onChange={(e) => updateBlock(index, 'location', e.target.value)}
                                  placeholder={language === 'es' ? 'Descripción adicional (opcional)' : 'Additional description (optional)'}
                                  className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-8"
                                />
                              </div>
                            )}
                            
                            {block.location_mode === 'manual' && (
                              <Input
                                value={block.location}
                                onChange={(e) => updateBlock(index, 'location', e.target.value)}
                                placeholder={language === 'es' ? 'Ej: Sala de casa, Cancha, Tienda (separa con coma)' : 'Ex: Living room, Stadium, Store (separate with comma)'}
                                className="bg-zinc-800/50 border-zinc-700 text-white text-sm h-8"
                              />
                            )}
                            
                            <div className="grid grid-cols-3 gap-2">
                              <Select value={String(block.quantity)} onValueChange={(v) => updateBlock(index, 'quantity', parseInt(v))}>
                                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)} className="text-zinc-300">{n}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Select value={block.characters} onValueChange={(v) => updateBlock(index, 'characters', v)}>
                                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  {characterOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Select value={block.gender} onValueChange={(v) => updateBlock(index, 'gender', v)}>
                                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  {genderOptions.map(opt => <SelectItem key={opt} value={opt} className="text-zinc-300">{opt}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" className="w-full border-zinc-700 text-zinc-300" onClick={addBlock}>
                          <Plus className="w-3 h-3 mr-1" /> {language === 'es' ? 'Agregar Bloque' : 'Add Block'}
                        </Button>
                        <p className="text-xs text-zinc-400 text-center">
                          Total a generar: <span className="text-lime-500 font-medium">{getTotalQuantity()} ideas</span>
                        </p>
                      </CardContent>
                    )}
                  </Card>

                  {/* Generate Button */}
                  <Button 
                    onClick={handlePreGenerate}
                    disabled={generating}
                    className="w-full bg-lime-500 text-black hover:bg-lime-600 font-semibold py-6 text-lg shadow-[0_4px_14px_0_rgba(132,204,22,0.39)]"
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('generating')}</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2" />{t('generate')} ({getTotalQuantity()})</>
                    )}
                  </Button>
                </div>
              </ScrollArea>
            </div>

            {/* Right Panel - Results */}
            <div className="lg:col-span-8">
              <Card className="bg-zinc-900/50 border-zinc-800 h-[calc(100vh-200px)]">
                <CardHeader className="border-b border-zinc-800">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-white">
                      {language === 'es' ? 'Resultados' : 'Results'}
                    </CardTitle>
                    {generatedIdeas.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveAllToFavorites}
                        className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                      >
                        <Heart className="w-4 h-4 mr-1" />
                        {language === 'es' ? 'Guardar Todos' : 'Save All'}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[calc(100vh-300px)]">
                    {/* Error Panel with Recommendations */}
                    {errorDetails && (
                      <div className="m-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 font-medium mb-2">{errorDetails.message}</p>
                        <p className="text-yellow-400 text-sm">{errorDetails.recommendation}</p>
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setFormData({...formData, model_provider: 'anthropic', model_name: 'claude-sonnet-4-5-20250929'});
                              setErrorDetails(null);
                            }}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs"
                          >
                            Cambiar a Claude
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setFormData({...formData, model_provider: 'gemini', model_name: 'gemini-3-flash-preview'});
                              setErrorDetails(null);
                            }}
                            className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs"
                          >
                            Cambiar a Gemini
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {generating ? (
                      <div className="flex flex-col items-center justify-center py-20">
                        <img src={MASCOT_URL} alt="Generating" className="w-32 h-32 object-contain animate-pulse mb-4" />
                        <p className="text-zinc-400">{t('generating')}</p>
                      </div>
                    ) : generatedIdeas.length === 0 && !errorDetails ? (
                      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                        <img src={MASCOT_URL} alt="Empty state" className="w-40 h-40 object-contain opacity-30 mb-4" />
                        <p className="text-zinc-500">
                          {language === 'es' 
                            ? 'Configura tu solicitud y genera ideas creativas' 
                            : 'Configure your request and generate creative ideas'}
                        </p>
                      </div>
                    ) : generatedIdeas.length > 0 ? (
                      <div className="divide-y divide-zinc-800">
                        {/* Saved indicator */}
                        <div className="flex items-center justify-between px-4 py-2 bg-lime-500/10 border-b border-lime-500/20">
                          <div className="flex items-center gap-2 text-lime-500 text-sm">
                            <BookmarkPlus className="w-4 h-4" />
                            {language === 'es' ? 'Ideas guardadas automáticamente en Biblioteca' : 'Ideas auto-saved to Library'}
                          </div>
                        </div>
                        {generatedIdeas.map((idea, index) => {
                          const isExpanded = expandedIds.has(idea.idea_id);
                          return (
                            <Collapsible key={idea.idea_id} open={isExpanded} onOpenChange={() => toggleExpanded(idea.idea_id)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-800/30 transition-colors">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="text-zinc-500">
                                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </div>
                                    <span className="text-lime-500 font-mono text-sm">#{index + 1}</span>
                                    {/* Shareability Score Badge */}
                                    {idea.shareability_score && (
                                      <div 
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                          idea.shareability_score.level === 'viral' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                          idea.shareability_score.level === 'high' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                                          idea.shareability_score.level === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                                          'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                                        }`}
                                        title={language === 'es' ? idea.shareability_score.label_es : idea.shareability_score.label_en}
                                      >
                                        {idea.shareability_score.level === 'viral' ? <Zap className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                        {idea.shareability_score.score}
                                      </div>
                                    )}
                                    <span className="text-white font-medium truncate">{idea.hook}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                    {/* Regenerate Button */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRegenerateIdea(idea.idea_id)}
                                      disabled={regeneratingId === idea.idea_id}
                                      className="h-8 px-2 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-50"
                                      title={language === 'es' ? 'Regenerar solo este' : 'Regenerate this one'}
                                    >
                                      {regeneratingId === idea.idea_id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="w-4 h-4" />
                                      )}
                                    </Button>
                                    {/* Tone Adjust Dropdown */}
                                    <Select onValueChange={(tone) => handleRegenerateIdea(idea.idea_id, tone)}>
                                      <SelectTrigger className="h-8 w-[110px] bg-zinc-800/50 border-zinc-700 text-xs text-zinc-400">
                                        <SelectValue placeholder="Ajustar tono" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-zinc-900 border-zinc-800">
                                        <SelectItem value="mas_negativo" className="text-xs text-zinc-300">Más negativo</SelectItem>
                                        <SelectItem value="mas_positivo" className="text-xs text-zinc-300">Más positivo</SelectItem>
                                        <SelectItem value="mas_urgente" className="text-xs text-zinc-300">Más urgente</SelectItem>
                                        <SelectItem value="mas_casual" className="text-xs text-zinc-300">Más casual</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    {/* Improve Button - show only for scores below 70 */}
                                    {idea.shareability_score && idea.shareability_score.score < 70 && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleImproveIdea(idea.idea_id)}
                                        disabled={improvingId === idea.idea_id}
                                        className="h-8 px-3 border-purple-500/50 text-purple-400 hover:bg-purple-500/10 disabled:opacity-50"
                                        data-testid="improve-idea-btn"
                                      >
                                        {improvingId === idea.idea_id ? (
                                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                        ) : (
                                          <Wand2 className="w-4 h-4 mr-1" />
                                        )}
                                        {language === 'es' ? 'Mejorar' : 'Improve'}
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleExportPDF(idea)}
                                      className="h-8 px-3 border-lime-500/50 text-lime-500 hover:bg-lime-500/10"
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      PDF
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleToggleFavorite(idea.idea_id)}
                                      className={`h-8 px-3 ${idea.is_favorite ? 'border-red-500 bg-red-500/20 text-red-500' : 'border-red-500/50 text-red-400 hover:bg-red-500/10'}`}
                                    >
                                      <Heart className={`w-4 h-4 mr-1 ${idea.is_favorite ? 'fill-current' : ''}`} />
                                      {idea.is_favorite ? (language === 'es' ? 'Favorito' : 'Favorite') : (language === 'es' ? 'Guardar' : 'Save')}
                                    </Button>
                                  </div>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="px-4 pb-4 space-y-4">
                                  {/* Copy Structure */}
                                  <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                      <h4 className="text-xs font-semibold text-white uppercase">Copy para Redes</h4>
                                      <div className="space-y-2">
                                        <div className="p-2 bg-lime-500/5 border border-lime-500/20 rounded">
                                          <p className="text-[10px] text-lime-500 uppercase mb-1">HOOK</p>
                                          <p className="text-sm text-zinc-200">{idea.hook}</p>
                                        </div>
                                        <div className="p-2 bg-zinc-800/50 rounded">
                                          <p className="text-[10px] text-zinc-500 uppercase mb-1">SOLUCIÓN</p>
                                          <p className="text-sm text-zinc-300">{idea.solution}</p>
                                        </div>
                                        <div className="p-2 bg-zinc-800/50 rounded">
                                          <p className="text-[10px] text-zinc-500 uppercase mb-1">BENEFICIO</p>
                                          <p className="text-sm text-zinc-300">{idea.benefit}</p>
                                        </div>
                                        <div className="p-2 bg-red-500/5 border border-red-500/20 rounded">
                                          <p className="text-[10px] text-red-500 uppercase mb-1">CIERRE</p>
                                          <p className="text-sm text-zinc-200">{idea.closing}</p>
                                        </div>
                                      </div>
                                      {/* Full Copy */}
                                      <div className="p-3 bg-zinc-800/30 rounded border border-zinc-700">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-[10px] text-zinc-400 uppercase">Copy Integrado</p>
                                          <Button variant="ghost" size="sm" onClick={() => handleCopy(idea.full_copy, `copy-${idea.idea_id}`)} className="h-6 text-xs text-zinc-400 hover:text-lime-500">
                                            {copiedId === `copy-${idea.idea_id}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                          </Button>
                                        </div>
                                        <p className="text-sm text-zinc-300 whitespace-pre-wrap">{idea.full_copy}</p>
                                      </div>
                                      
                                      {/* Shareability Score Details */}
                                      {idea.shareability_score && (
                                        <div className={`p-3 rounded border ${
                                          idea.shareability_score.level === 'viral' ? 'bg-purple-500/10 border-purple-500/30' :
                                          idea.shareability_score.level === 'high' ? 'bg-green-500/10 border-green-500/30' :
                                          idea.shareability_score.level === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                          'bg-zinc-800/30 border-zinc-700'
                                        }`}>
                                          <div className="flex items-center justify-between mb-3">
                                            <p className="text-[10px] uppercase flex items-center gap-1 ${
                                              idea.shareability_score.level === 'viral' ? 'text-purple-400' :
                                              idea.shareability_score.level === 'high' ? 'text-green-400' :
                                              idea.shareability_score.level === 'medium' ? 'text-yellow-400' :
                                              'text-zinc-400'
                                            }">
                                              <Share2 className="w-3 h-3" />
                                              Shareability Score
                                            </p>
                                            <span className={`text-2xl font-bold ${
                                              idea.shareability_score.level === 'viral' ? 'text-purple-400' :
                                              idea.shareability_score.level === 'high' ? 'text-green-400' :
                                              idea.shareability_score.level === 'medium' ? 'text-yellow-400' :
                                              'text-zinc-400'
                                            }`}>
                                              {idea.shareability_score.score}/100
                                            </span>
                                          </div>
                                          <p className={`text-sm font-medium mb-3 ${
                                            idea.shareability_score.level === 'viral' ? 'text-purple-300' :
                                            idea.shareability_score.level === 'high' ? 'text-green-300' :
                                            idea.shareability_score.level === 'medium' ? 'text-yellow-300' :
                                            'text-zinc-300'
                                          }`}>
                                            {language === 'es' ? idea.shareability_score.label_es : idea.shareability_score.label_en}
                                          </p>
                                          <div className="grid grid-cols-2 gap-2 text-xs">
                                            {idea.shareability_score.factors && Object.entries(idea.shareability_score.factors).map(([key, value]) => (
                                              <div key={key} className="flex justify-between items-center">
                                                <span className="text-zinc-500 capitalize">{key.replace(/_/g, ' ')}</span>
                                                <span className="text-zinc-300">{value}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Scene & Image Prompt */}
                                    <div className="space-y-3">
                                      {/* Visual Image Parameters Card */}
                                      {idea.image_prompt && (
                                        <div className="space-y-3">
                                          <h4 className="text-xs font-semibold text-white uppercase mb-2">Parámetros de Imagen</h4>
                                          
                                          {/* Parameter Chips */}
                                          <div className="flex flex-wrap gap-2">
                                            {/* Personaje Chip */}
                                            <div className="px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                              <div className="flex items-center gap-1.5">
                                                <User className="w-3 h-3 text-purple-400" />
                                                <span className="text-[10px] text-purple-400 uppercase font-medium">Personaje</span>
                                              </div>
                                              <p className="text-xs text-white mt-1">{getCharacterSummary(idea.image_prompt.characters)}</p>
                                            </div>
                                            
                                            {/* Emoción Chip */}
                                            {idea.image_prompt.emotion && (
                                              <div className="px-3 py-2 bg-pink-500/10 border border-pink-500/30 rounded-lg">
                                                <div className="flex items-center gap-1.5">
                                                  <Heart className="w-3 h-3 text-pink-400" />
                                                  <span className="text-[10px] text-pink-400 uppercase font-medium">Emoción</span>
                                                </div>
                                                <p className="text-xs text-white mt-1">{idea.image_prompt.emotion}</p>
                                              </div>
                                            )}
                                            
                                            {/* Prop Principal Chip */}
                                            {extractMainProp(idea.image_prompt.scene) && (
                                              <div className="px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                                                <div className="flex items-center gap-1.5">
                                                  <Target className="w-3 h-3 text-orange-400" />
                                                  <span className="text-[10px] text-orange-400 uppercase font-medium">Prop Principal</span>
                                                </div>
                                                <p className="text-xs text-white mt-1">{extractMainProp(idea.image_prompt.scene)}</p>
                                              </div>
                                            )}
                                            
                                            {/* Cámara Chip */}
                                            {idea.image_prompt.camera && (
                                              <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                                <div className="flex items-center gap-1.5">
                                                  <Camera className="w-3 h-3 text-blue-400" />
                                                  <span className="text-[10px] text-blue-400 uppercase font-medium">Cámara</span>
                                                </div>
                                                <p className="text-xs text-white mt-1">
                                                  {idea.image_prompt.camera.shot} • {idea.image_prompt.camera.lens} • {idea.image_prompt.camera.aspect_ratio}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* Locación Chip - Special Styling with Green Border */}
                                          {(idea.image_prompt.location || getCurrentLocation(idea)) && (
                                            <div className={`p-3 rounded-lg border-2 ${
                                              isLocationEdited(idea.idea_id) 
                                                ? 'bg-green-500/10 border-green-500/50' 
                                                : 'bg-lime-500/5 border-lime-500/40'
                                            }`}>
                                              <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-2">
                                                  <MapPin className="w-3 h-3 text-lime-400" />
                                                  <span className="text-[10px] text-lime-400 uppercase font-medium">Locación</span>
                                                  {/* Warning or Edited tag */}
                                                  {isLocationEdited(idea.idea_id) ? (
                                                    <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-[9px] rounded font-medium">
                                                      ✓ editado
                                                    </span>
                                                  ) : detectLocationIncoherence(
                                                    extractMainProp(idea.image_prompt.scene),
                                                    getCurrentLocation(idea)
                                                  ) && (
                                                    <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[9px] rounded font-medium flex items-center gap-1">
                                                      <AlertTriangle className="w-2.5 h-2.5" />
                                                      revisar
                                                    </span>
                                                  )}
                                                </div>
                                                <Button 
                                                  variant="ghost" 
                                                  size="sm" 
                                                  onClick={() => setEditingLocationId(idea.idea_id)}
                                                  className="h-5 px-1.5 text-zinc-400 hover:text-lime-400"
                                                >
                                                  <Pencil className="w-3 h-3" />
                                                </Button>
                                              </div>
                                              
                                              {/* Editable Location Field */}
                                              {editingLocationId === idea.idea_id ? (
                                                <Input
                                                  autoFocus
                                                  defaultValue={getCurrentLocation(idea)}
                                                  onBlur={(e) => handleLocationEdit(idea.idea_id, e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      handleLocationEdit(idea.idea_id, e.target.value);
                                                    }
                                                    if (e.key === 'Escape') {
                                                      setEditingLocationId(null);
                                                    }
                                                  }}
                                                  className="text-xs bg-zinc-800/50 border-lime-500/50 text-white h-7 mt-1"
                                                  placeholder="Ej: sala de casa, cocina, oficina..."
                                                />
                                              ) : (
                                                <p className="text-xs text-white mt-1 cursor-pointer hover:text-lime-300" onClick={() => setEditingLocationId(idea.idea_id)}>
                                                  {getCurrentLocation(idea)}
                                                </p>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Scene Summary */}
                                          <div className="p-2 bg-zinc-800/30 rounded border border-zinc-700/50">
                                            <p className="text-xs text-zinc-300">
                                              <span className="text-zinc-500">Escena:</span>{' '}
                                              {getCharacterSummary(idea.image_prompt.characters)} 
                                              {extractMainProp(idea.image_prompt.scene) && ` con ${extractMainProp(idea.image_prompt.scene)}`}
                                              {' en '}<span className="text-lime-400">{getCurrentLocation(idea)}</span>
                                            </p>
                                          </div>
                                          
                                          {/* Action Buttons */}
                                          <div className="flex gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => setShowJsonId(showJsonId === idea.idea_id ? null : idea.idea_id)}
                                              className="text-xs border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
                                            >
                                              {showJsonId === idea.idea_id ? (
                                                <><EyeOff className="w-3 h-3 mr-1.5" /> Ocultar JSON</>
                                              ) : (
                                                <><Eye className="w-3 h-3 mr-1.5" /> Ver JSON completo</>
                                              )}
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-xs border-lime-500/50 text-lime-400 hover:text-lime-300 hover:bg-lime-500/10"
                                            >
                                              <ImageLucide className="w-3 h-3 mr-1.5" />
                                              Generar imagen con esta locación
                                            </Button>
                                          </div>
                                          
                                          {/* Collapsible JSON View */}
                                          {showJsonId === idea.idea_id && (
                                            <div className="p-3 bg-black rounded border border-zinc-800 mt-2">
                                              <div className="flex items-center justify-between mb-2">
                                                <p className="text-[10px] text-lime-400">Image Prompt JSON</p>
                                                <Button variant="ghost" size="sm" onClick={() => handleCopy(JSON.stringify(idea.image_prompt, null, 2), `prompt-${idea.idea_id}`)} className="h-6 text-xs text-zinc-400 hover:text-lime-500">
                                                  {copiedId === `prompt-${idea.idea_id}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                                </Button>
                                              </div>
                                              <pre className="text-xs text-lime-400 font-mono overflow-x-auto whitespace-pre-wrap max-h-60">
                                                {JSON.stringify(idea.image_prompt, null, 2)}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {idea.scene_data && Object.keys(idea.scene_data).length > 0 && (
                                        <div>
                                          <h4 className="text-xs font-semibold text-white uppercase mb-2">Escena (datos adicionales)</h4>
                                          <div className="p-3 bg-zinc-800/30 rounded">
                                            <pre className="text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap">
                                              {JSON.stringify(idea.scene_data, null, 2)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {idea.branding_info && Object.keys(idea.branding_info).length > 0 && (
                                        <div>
                                          <h4 className="text-xs font-semibold text-white uppercase mb-2">Branding (overlay)</h4>
                                          <div className="p-3 bg-zinc-800/30 rounded">
                                            <pre className="text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap">
                                              {JSON.stringify(idea.branding_info, null, 2)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          );
                        })}
                      </div>
                    ) : null}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-lime-500" />
                {language === 'es' ? 'Confirmar Generación' : 'Confirm Generation'}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {language === 'es' ? 'Así interpretó la IA tu solicitud:' : 'This is how the AI interpreted your request:'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Idea Central */}
              {getConfirmationSummary().ideaCentral && (
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <div className="text-xs font-medium text-purple-400 uppercase mb-1">Idea Central</div>
                  <div className="text-sm text-white">{getConfirmationSummary().ideaCentral}</div>
                </div>
              )}

              {/* Riesgo Creativo */}
              <div className={`p-3 rounded-lg border ${
                briefData.riesgoCreativo === 'seguro' 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : briefData.riesgoCreativo === 'experimental'
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
              }`}>
                <div className="text-xs font-medium uppercase mb-1" style={{
                  color: briefData.riesgoCreativo === 'seguro' ? '#4ade80' : 
                         briefData.riesgoCreativo === 'experimental' ? '#f87171' : '#facc15'
                }}>
                  Riesgo Creativo
                </div>
                <div className="text-sm text-white font-medium">{getConfirmationSummary().riesgo}</div>
                <div className="text-xs text-zinc-400 mt-1">{getConfirmationSummary().riesgoDesc}</div>
              </div>

              {/* Emociones */}
              {getConfirmationSummary().emociones.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-zinc-500 uppercase">Emociones a generar</div>
                  <div className="flex flex-wrap gap-1">
                    {getConfirmationSummary().emociones.map((e, i) => (
                      <Badge key={i} variant="outline" className="border-purple-500/50 text-purple-400 text-xs">{e}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Hook Style */}
              {getConfirmationSummary().hookStyle.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-zinc-500 uppercase">Estilo de Hook</div>
                  <div className="flex flex-wrap gap-1">
                    {getConfirmationSummary().hookStyle.map((h, i) => (
                      <Badge key={i} variant="outline" className="border-lime-500/50 text-lime-400 text-xs">{h}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Evitar */}
              {getConfirmationSummary().evitar.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-zinc-500 uppercase">Restricciones</div>
                  <div className="flex flex-wrap gap-1">
                    {getConfirmationSummary().evitar.map((e, i) => (
                      <Badge key={i} variant="outline" className="border-red-500/50 text-red-400 text-xs">{e}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Output Format */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <div className="text-xs font-medium text-zinc-500 uppercase mb-1">Formato de salida</div>
                <div className="text-sm text-white">{getConfirmationSummary().cantidad} ideas creativas</div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                Modificar
              </Button>
              <Button
                onClick={handleGenerate}
                className="bg-lime-500 text-black hover:bg-lime-600"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Confirmar y Generar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
